import { useState, FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { Link } from "react-router-dom";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Select } from "../../components/Select/Select";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { useAuth } from "../auth/useAuth";
import { ApiError, describeError } from "../../lib/errors";
import { createPlatformInvite } from "./api";
import styles from "./UsersPage.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Platform-level invite modal. Mockup L678-L712.
 *
 * Two-field form (Email + Role) with a contextual Project select that becomes
 * required when Role=Member and is rendered as a disabled placeholder when
 * Role=Super admin (so the page layout doesn't jump). Closes with an info
 * Alert linking to the Invites page for tracking / revocation.
 */
export function InviteAdminModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();
  const { projects } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"super_admin" | "project_user">(
    "super_admin",
  );
  const [gameId, setGameId] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: createPlatformInvite,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ["invites"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      // Project-scoped invite — refresh that project's activity feed too so
      // a stale tab returning to the project overview sees the new event.
      if (variables.game_id) {
        void qc.invalidateQueries({
          queryKey: ["project", variables.game_id, "activity"],
        });
      }
      push({
        variant: "success",
        message: `Invite sent to ${variables.email}`,
      });
      reset();
      onClose();
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function reset() {
    setEmail("");
    setRole("super_admin");
    setGameId("");
    setEmailError(null);
    setProjectError(null);
    setSubmitError(null);
  }

  function submit() {
    setEmailError(null);
    setProjectError(null);
    setSubmitError(null);
    const trimmed = email.trim().toLowerCase();
    let bad = false;
    if (!trimmed || !trimmed.includes("@")) {
      setEmailError("Enter a valid email address.");
      bad = true;
    }
    if (role === "project_user" && !gameId) {
      setProjectError("Select a project for Member role.");
      bad = true;
    }
    if (bad) return;
    mut.mutate({
      email: trimmed,
      role,
      game_id: role === "project_user" ? gameId : undefined,
    });
  }

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleRoleChange(v: string) {
    setRole(v as "super_admin" | "project_user");
    setProjectError(null);
    setSubmitError(null);
  }

  const memberRole = role === "project_user";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Invite admin"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={mut.isPending}
            loading={mut.isPending}
          >
            {mut.isPending ? (
              "Sending…"
            ) : (
              <>
                <Send size={14} aria-hidden="true" />
                <span>Send invite</span>
              </>
            )}
          </Button>
        </>
      }
    >
      <form onSubmit={onFormSubmit} noValidate>
        {submitError && (
          <div className={styles.formAlert}>
            <Alert variant="danger">{submitError}</Alert>
          </div>
        )}
        <Input
          label="Email address"
          type="email"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(null);
          }}
          placeholder="colleague@studio.com"
          error={emailError ?? undefined}
          required
        />
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="invite-role">
            Role
          </label>
          <Select
            id="invite-role"
            value={role}
            onChange={handleRoleChange}
            options={[
              { value: "super_admin", label: "Super admin" },
              { value: "project_user", label: "Member" },
            ]}
          />
          <div className={styles.formHelper}>
            Super admin — platform-wide access. Member — invite as Member of a
            specific project (assigned below).
          </div>
        </div>
        <div className={styles.formField}>
          <div className={styles.formLabelRow}>
            <label className={styles.formLabel} htmlFor="invite-project">
              Project
            </label>
            {memberRole ? (
              <span className={styles.formLabelHintRequired}>required</span>
            ) : (
              <span className={styles.formLabelHint}>
                required for Member only
              </span>
            )}
          </div>
          {memberRole ? (
            <Select
              id="invite-project"
              value={gameId}
              onChange={(v) => {
                setGameId(v);
                setProjectError(null);
              }}
              placeholder="Select project…"
              options={projects.map((p) => ({ value: p.id, label: p.name }))}
            />
          ) : (
            <div
              id="invite-project"
              className={styles.projectFieldDisabled}
              aria-disabled="true"
            >
              — not applicable for Super admin —
            </div>
          )}
          {projectError && (
            <div className={styles.formError}>{projectError}</div>
          )}
        </div>
        <div className={styles.formAlert}>
          <Alert variant="info">
            Invite expires in 72 hours. Track or revoke from{" "}
            <Link to="/invites" className={styles.alertLink}>
              Platform → Invites
            </Link>
            .
          </Alert>
        </div>
      </form>
    </Modal>
  );
}
